import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  sendEmailVerification,
  UserCredential
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "./config";

export type AllowedUserRole = "ADMIN" | "CLIENT" | "USER" | "PUBLIC";

const ADMIN_EMAILS = [
  "admin@verveailabs.com",
  "manjushukla89@gmail.com",
  "rds087@gmail.com"
];

/**
 * Normalizes user role string to typed AllowedUserRole
 */
export function normalizeUserRole(rawRole?: string | null, email?: string | null): AllowedUserRole {
  const normEmail = (email || "").trim().toLowerCase();
  if (ADMIN_EMAILS.includes(normEmail)) {
    return "ADMIN";
  }

  const role = (rawRole || "").trim().toUpperCase();
  if (role === "SUPER_ADMIN" || role === "ADMIN" || role === "CA") {
    return "ADMIN";
  }
  if (role === "CLIENT") {
    return "CLIENT";
  }
  if (role === "USER") {
    return "USER";
  }
  return "PUBLIC";
}

/**
 * Determines a user's role from token, users collection, or clients collection
 */
export async function resolveUserRole(user: User): Promise<{
  role: AllowedUserRole;
  clientId: string;
  companyName?: string;
  isAuthorized: boolean;
}> {
  const normEmail = (user.email || "").trim().toLowerCase();

  // 1. Check Admin Email List
  if (ADMIN_EMAILS.includes(normEmail)) {
    return {
      role: "ADMIN",
      clientId: user.uid,
      companyName: "Verve AI Labs (Admin Console)",
      isAuthorized: true
    };
  }

  // 2. Check Custom Claims in Auth Token
  try {
    const tokenResult = await user.getIdTokenResult();
    const claimRole = (tokenResult.claims.role as string || "").toUpperCase();
    if (claimRole === "ADMIN" || claimRole === "SUPER_ADMIN" || claimRole === "CA") {
      return {
        role: "ADMIN",
        clientId: user.uid,
        companyName: "Verve AI Labs (Admin Console)",
        isAuthorized: true
      };
    }
    if (claimRole === "CLIENT") {
      return {
        role: "CLIENT",
        clientId: (tokenResult.claims.assignedClientId as string) || user.uid,
        isAuthorized: true
      };
    }
  } catch (err) {
    console.warn("Could not retrieve custom claims:", err);
  }

  // 3. Check /users/{uid} document in Firestore
  try {
    const userDocRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userDocRef);
    if (userSnap.exists()) {
      const data = userSnap.data();
      const normRole = normalizeUserRole(data.role, user.email);
      if (normRole === "ADMIN") {
        return {
          role: "ADMIN",
          clientId: user.uid,
          companyName: data.companyName || "Admin Console",
          isAuthorized: true
        };
      }
      if (normRole === "CLIENT") {
        return {
          role: "CLIENT",
          clientId: data.assignedClientId || user.uid,
          companyName: data.companyName || data.name,
          isAuthorized: true
        };
      }
    }
  } catch (err) {
    console.warn("Could not read users doc:", err);
  }

  // 4. Check if /clients/{uid} exists directly
  try {
    const clientDocRef = doc(db, "clients", user.uid);
    const clientSnap = await getDoc(clientDocRef);
    if (clientSnap.exists()) {
      const clientData = clientSnap.data();
      return {
        role: "CLIENT",
        clientId: user.uid,
        companyName: clientData.name || clientData.companyName,
        isAuthorized: true
      };
    }
  } catch (err) {
    console.warn("Could not read direct client doc:", err);
  }

  // 5. Query /clients where email matches user.email
  if (user.email) {
    try {
      const clientsCol = collection(db, "clients");
      const q = query(clientsCol, where("email", "==", user.email));
      const matchSnap = await getDocs(q);
      if (!matchSnap.empty) {
        const clientDoc = matchSnap.docs[0];
        const clientData = clientDoc.data();
        return {
          role: "CLIENT",
          clientId: clientDoc.id,
          companyName: clientData.name || clientData.companyName,
          isAuthorized: true
        };
      }
    } catch (err) {
      console.warn("Could not query client by email:", err);
    }
  }

  // Default: Standard registered user without CLIENT or ADMIN privileges
  return {
    role: "USER",
    clientId: user.uid,
    isAuthorized: false
  };
}

export const signUpWithEmail = async (email: string, pass: string, displayName?: string, companyName?: string): Promise<UserCredential> => {
  const cred = await createUserWithEmailAndPassword(auth, email, pass);
  // Create /users/{uid} document with default CLIENT intake status
  try {
    const userDocRef = doc(db, "users", cred.user.uid);
    await setDoc(userDocRef, {
      uid: cred.user.uid,
      email: cred.user.email,
      name: displayName || email.split("@")[0],
      companyName: companyName || "",
      role: "CLIENT", // Onboarding clients default to CLIENT role for self-service compliance
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });

    // Also initialize /clients/{uid} record
    const clientDocRef = doc(db, "clients", cred.user.uid);
    await setDoc(clientDocRef, {
      id: cred.user.uid,
      name: companyName || displayName || email.split("@")[0],
      email: cred.user.email,
      status: "active",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (e) {
    console.warn("Could not initialize user/client profile:", e);
  }
  return cred;
};

export const signInWithEmail = (email: string, pass: string): Promise<UserCredential> => {
  return signInWithEmailAndPassword(auth, email, pass);
};

export const signInWithGoogle = async (): Promise<UserCredential> => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({
    prompt: "select_account"
  });
  const cred = await signInWithPopup(auth, provider);

  // If user doc doesn't exist, create it
  try {
    const userDocRef = doc(db, "users", cred.user.uid);
    const snap = await getDoc(userDocRef);
    if (!snap.exists()) {
      const isAutoAdmin = ADMIN_EMAILS.includes((cred.user.email || "").toLowerCase());
      await setDoc(userDocRef, {
        uid: cred.user.uid,
        email: cred.user.email,
        name: cred.user.displayName || cred.user.email?.split("@")[0] || "Client User",
        role: isAutoAdmin ? "ADMIN" : "CLIENT",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      if (!isAutoAdmin) {
        const clientDocRef = doc(db, "clients", cred.user.uid);
        await setDoc(clientDocRef, {
          id: cred.user.uid,
          name: cred.user.displayName || "Client Organization",
          email: cred.user.email,
          status: "active",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true });
      }
    }
  } catch (e) {
    console.warn("Could not sync Google sign-in user profile:", e);
  }

  return cred;
};

export const signOutUser = (): Promise<void> => {
  return signOut(auth);
};

export const sendResetPassword = async (email: string): Promise<void> => {
  const cleanEmail = (email || "").trim().toLowerCase();
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "https://gst.verveai.co";

  // 1. Primary: Dispatch enterprise branded HTML email via Verve AI Labs Custom Email Service
  try {
    const res = await fetch("https://admin.verveailabs.com/api/auth/send-custom-email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        type: "PASSWORD_RESET",
        email: cleanEmail,
        redirectUrl: `${currentOrigin}/`,
      }),
    });

    if (res.ok) {
      const data = await res.json();
      if (data.success) {
        return;
      }
    }
  } catch (customErr) {
    console.warn("Custom branded email service unavailable, falling back to Firebase Auth SDK:", customErr);
  }

  // 2. Fallback: Firebase Auth Client SDK with ActionCodeSettings redirecting to current origin
  const actionCodeSettings = {
    url: `${currentOrigin}/`,
    handleCodeInApp: false,
  };
  return sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
};

export const sendVerificationEmail = async (user: User): Promise<void> => {
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "https://gst.verveai.co";

  if (user.email) {
    try {
      const res = await fetch("https://admin.verveailabs.com/api/auth/send-custom-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "EMAIL_VERIFICATION",
          email: user.email.trim().toLowerCase(),
          recipientName: user.displayName || user.email.split("@")[0],
          redirectUrl: `${currentOrigin}/`,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) return;
      }
    } catch (err) {
      console.warn("Custom verification email failed, falling back to Firebase Auth SDK:", err);
    }
  }

  const actionCodeSettings = {
    url: `${currentOrigin}/`,
    handleCodeInApp: false,
  };
  return sendEmailVerification(user, actionCodeSettings);
};

export const onAuthStateListener = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};
